// Single source of truth for static page SEO meta strings.
// Both server (src/seo/routes.ts → injected into raw HTML) and client (each
// page component's <SEO> props → React Helmet at runtime) import from here.
//
// Edit a title or description ONCE and both layers update on the next build.
//
// Dynamic pages (/product/:id, /blog/:slug) are NOT in this file — their meta
// is built from per-row data: products via the DB (src/seo/resolveProduct.ts
// + client/src/pages/ProductDetailPage.tsx) and blog via src/shared/blog.ts.

export interface PageSeoMeta {
  title: string;
  description: string;
  canonical: string;
}

export const pageSeoMeta = {
  home: {
    title:
      "Nicotine Pouches in India — ZYN, VELO, Pablo, White Fox | ZeroSmoke",
    description:
      "ZeroSmoke — Shop authentic tobacco-free nicotine pouches in India. ZYN, VELO, Pablo, White Fox & CLEW. Tobacco-free, smoke-free, discreet, fast delivery nationwide.",
    canonical: "/",
  },

  store: {
    title: "Shop Nicotine Pouches Online India | ZeroSmoke Store",
    description:
      "Browse every flavour and strength from ZYN, VELO, Pablo, White Fox & CLEW. Tobacco-free, authentic, with fast discreet delivery across India.",
    canonical: "/store",
  },

  about: {
    title:
      "About ZeroSmoke — India's Trusted Nicotine Pouch Store | Smoke-Free Alternatives",
    description:
      "ZeroSmoke is India's premium destination for tobacco-free nicotine pouches. We bring world-class brands like ZYN, VELO, Pablo, White Fox & CLEW — 100% authentic, smoke-free & discreet. Join India's harm reduction movement.",
    canonical: "/about",
  },

  contact: {
    title: "Contact ZeroSmoke — Customer Support & Help",
    description:
      "Need help with your nicotine pouch order? Contact ZeroSmoke for product questions, shipping, returns or bulk orders. Fast email support.",
    canonical: "/contact",
  },

  wholesale: {
    title: "Wholesale & Bulk Nicotine Pouches — Trade Pricing | ZeroSmoke",
    description:
      "Buy nicotine pouches wholesale from ZeroSmoke. Bulk discounts, minimum order of 100 pcs, and transportation arranged for retailers. Submit a wholesale inquiry for trade pricing on ZYN, VELO, Pablo, White Fox & CLEW.",
    canonical: "/wholesale",
  },

  rewards: {
    title: "ZeroSmoke Rewards — ZS Coins, Ranks & Referrals",
    description:
      "Earn ZS Coins on every delivered order, level up your rank from Aagman to Asur, and refer friends for bonus coins. Learn how ZeroSmoke Rewards works and start earning.",
    canonical: "/rewards",
  },

  faq: {
    title: "Nicotine Pouch FAQ — Common Questions Answered | ZeroSmoke.in",
    description:
      "Frequently asked questions about nicotine pouches: how to use them, available brands and strengths, safety, shipping across India, returns and more. Everything you need to know before you buy at ZeroSmoke.",
    canonical: "/faq",
  },

  blog: {
    title: "ZeroSmoke Blog — Nicotine Pouch Guides for India",
    description:
      "Guides, brand reviews, comparisons and lifestyle articles about nicotine pouches in India. Velo, ZYN, Pablo, White Fox and more.",
    canonical: "/blog",
  },

  cityIndex: {
    title: "Nicotine Pouch Delivery Across India — 40 Cities | ZeroSmoke",
    description:
      "ZeroSmoke ships nicotine pouches across India. Explore delivery guides and popular picks for 40 cities including all 28 state capitals. Cash on Delivery available.",
    canonical: "/city",
  },

  login: {
    title: "Login — ZeroSmoke.in | Your Nicotine Pouch Account",
    description:
      "Sign in to your ZeroSmoke account. Track orders, manage your profile and shop premium nicotine pouches from ZYN, VELO, Pablo & more.",
    canonical: "/login",
  },

  signup: {
    title: "Create Account — ZeroSmoke.in | Join India's Smoke-Free Community",
    description:
      "Sign up at ZeroSmoke.in to shop tobacco-free nicotine pouches. Exclusive deals, order tracking & fast delivery across India. Join now!",
    canonical: "/signup",
  },

  verifyEmail: {
    title: "Verify Email — ZeroSmoke.in",
    description:
      "Verify your email address to complete your ZeroSmoke registration. Enter the OTP sent to your inbox.",
    canonical: "/verify-email",
  },

  forgotPassword: {
    title: "Reset Password — ZeroSmoke.in",
    description:
      "Forgot your ZeroSmoke password? Reset it securely via OTP email verification. Get back to shopping nicotine pouches in minutes.",
    canonical: "/forgot-password",
  },

  cart: {
    title: "Shopping Cart — ZeroSmoke.in",
    description:
      "Review your nicotine pouch order. Apply coupons, update quantities and checkout securely. Free shipping on orders above ₹5,000 at ZeroSmoke.in",
    canonical: "/cart",
  },

  cartEmpty: {
    title: "Your Cart — ZeroSmoke.in",
    description:
      "Your shopping cart is empty. Browse premium nicotine pouches from ZYN, VELO, Pablo & more at ZeroSmoke.in",
    canonical: "/cart",
  },

  checkout: {
    title: "Secure Checkout — ZeroSmoke.in",
    description:
      "Complete your nicotine pouch order securely. Fast shipping across India with discreet packaging. 100% authentic products guaranteed.",
    canonical: "/checkout",
  },

  profile: {
    title: "My Profile — ZeroSmoke.in",
    description:
      "Manage your ZeroSmoke profile, update personal details and change your password securely.",
    canonical: "/profile",
  },

  orders: {
    title: "My Orders — ZeroSmoke.in | Track Your Nicotine Pouch Orders",
    description:
      "Track your ZeroSmoke orders, view order history, get delivery status updates and leave product reviews. All your nicotine pouch orders in one place.",
    canonical: "/orders",
  },

  policyRefund: {
    title: "Refund & Return Policy — ZeroSmoke.in | Nicotine Pouches India",
    description:
      "ZeroSmoke refund policy: 7-day return window on unopened nicotine pouches. Easy refund process, original packaging required. Read our full return and refund terms.",
    canonical: "/policies/refund",
  },

  policyShipping: {
    title: "Shipping Policy — ZeroSmoke.in | Fast Delivery Across India",
    description:
      "ZeroSmoke ships nicotine pouches across India. 1-2 day processing, discreet packaging, free shipping on orders above ₹5,000. Check delivery times for your city.",
    canonical: "/policies/shipping",
  },

  policyTerms: {
    title: "Terms of Service — ZeroSmoke.in | Nicotine Pouches India",
    description:
      "Read ZeroSmoke's terms of service. 18+ age requirement, product authenticity guarantee, order policies, payment terms & conditions for buying nicotine pouches online in India.",
    canonical: "/policies/terms",
  },

  policyPrivacy: {
    title: "Privacy Policy — ZeroSmoke.in | Data Protection & Security",
    description:
      "ZeroSmoke privacy policy: how we collect, use and protect your personal data. No card storage, secure transactions, opt-out options for promotions. Your privacy matters.",
    canonical: "/policies/privacy",
  },

  notFound: {
    title: "404 Page Not Found — ZeroSmoke.in",
    description:
      "The page you're looking for doesn't exist. Head back to ZeroSmoke.in to shop premium nicotine pouches from ZYN, VELO, Pablo & more.",
    canonical: "/404",
  },
} as const satisfies Record<string, PageSeoMeta>;

export type PageSeoKey = keyof typeof pageSeoMeta;
